import React, { useMemo, useState } from 'react';
import { RCButton, RCIcon } from '@reverbdotcom/cadence/components';
import { TrophyIcon } from '@phosphor-icons/react';
import { NudgeExploreIcon } from '@reverbdotcom/cadence/icons/react';
import { Listing, NextBestAction } from '@reverbdotcom/commons/src/gql/graphql';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';

import BumpModalV2WithController from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import {
  buildClickedEvent,
  INSIGHT,
  trackClicked,
  useTrackingContext,
  useTrackImpression,
} from './sellerRecommendationTracking';
import type { MParticleEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';

interface Props {
  listing: Listing;
  nextBestAction: NextBestAction;
}

export const COMPONENT_NAME = 'NBAPromoteWithBumpPresentment';

export default function NBAPromoteWithBumpPresentment({ listing, nextBestAction }: Props) {
  const [showBumpModal, setShowBumpModal] = useState(false);
  const trackingContext = useTrackingContext();

  const featuredWinner = !!listing.featuredListingInsight?.featuredWinner;
  const insight = featuredWinner ? INSIGHT.TOP_PICK_WINNER : INSIGHT.TOP_PICK_ELIGIBLE;

  const recommendedRate = listing.featuredListingInsight?.recommendedBumpRate;
  const bumpRate = listing.bumpRate?.rate || 0;

  const details = { recommendedBumpRate: recommendedRate ?? undefined };

  // Memoized: this lands in the bump modal's success effect deps, and a fresh object each
  // render would re-run it -- which fires trackFollowed and dispatches a toast.
  const bumpSource = useMemo(
    () => ({ insight, componentName: COMPONENT_NAME, details: { recommendedBumpRate: recommendedRate ?? undefined } }),
    [insight, recommendedRate],
  );

  useTrackImpression(COMPONENT_NAME, insight, listing.id, details);

  return (
    <>
      <div className="seller-listing-insight-presentment">
        <span className={featuredWinner ?
          'seller-listing-insight-presentment__top-pick-winner' :
          'seller-listing-insight-presentment__top-pick-eligible'}
        >
          <RCIcon icon={featuredWinner ? <TrophyIcon /> : <NudgeExploreIcon />} size="inline-prominent" spacing="right" color={featuredWinner ? 'text-success' : 'accent-loud'} />
          <RCTextUtility size="350" weight="bold">
            <SanitizedRender html={nextBestAction.title ?? ''} />
          </RCTextUtility>
        </span>
        <MaybeLink
          label={nextBestAction.subtitle}
          url={nextBestAction.subtitleUrl}
          clickEvent={buildClickedEvent(COMPONENT_NAME, insight, listing.id, trackingContext, details)}
        />
        <MaybeLink
          label={nextBestAction.footerText}
          url={nextBestAction.footerUrl}
          clickEvent={buildClickedEvent(COMPONENT_NAME, insight, listing.id, trackingContext, details)}
        />
        {!!nextBestAction.ctaText && (
          <RCButton
            variant="outlined"
            size="small"
            onClick={() => {
              trackClicked(COMPONENT_NAME, insight, listing.id, trackingContext, details);
              setShowBumpModal(true);
            }}
            fullWidth
          >
            {nextBestAction.ctaText}
          </RCButton>
        )}
      </div>

      {!!nextBestAction.ctaText && (
        <BumpModalV2WithController
          listingId={listing.id}
          bid={bumpRatePercentToDecimal(bumpRate)}
          showModal={showBumpModal}
          setShowModal={setShowBumpModal}
          listing={listing}
          source={bumpSource}
        />
      )}
    </>
  );
}

function MaybeLink({ label, url, clickEvent }: { label?: string | null, url?: string | null, clickEvent: MParticleEvent }) {
  if (!label) {
    return <></>;
  }

  return (
    <RCTextUtility size="350">
      {url ? (
        <CoreLink
          to={url}
          className="fg-color-text-primary"
          target="_blank"
          rel="noreferrer"
          clickEvent={clickEvent}
        >
          <SanitizedRender html={label} />
        </CoreLink>
      ) : (
        <SanitizedRender html={label} />
      )}
    </RCTextUtility>
  );
}
